/**
 * Hook d’orchestration :
 * - lance les requêtes (dossiers validés + compteurs dashboard)
 * - prépare les inputs (high risk country codes)
 * - calcule les statistiques via une fonction pure (calculators)
 */
import { useCallback, useMemo, useState } from "react";

import { CustomFolderFileType, Status } from "src/core/constants/folders";
import { CustomRiskLevel } from "src/core/constants/risk";
import { useFolderFilesCountLazyQuery } from "src/core/gql/graphql";
import { useCountryWithRiskOptions } from "src/core/hooks/common/useCountryWithRiskOptions";
import { calculateStatistics } from "src/core/hooks/folders-list/folder-risk-statistics/calculator";
import type { ValidatedFoldersStatistics } from "src/core/hooks/folders-list/folder-risk-statistics/types";
import { extractDashboardCounts } from "src/core/hooks/folders-list/folder-risk-statistics/utils";
import { useGetValidatedFolderFiles } from "src/core/hooks/folders-list/useGetValidatedFolderFiles";

/**
 * Résultat de la fonction `fetchAndCalculate`.
 */
type FetchAndCalculateResult = {
  statistics: ValidatedFoldersStatistics | null;
  error: Error | null;
};

/**
 * Hook pour calculer les statistiques des dossiers validés.
 */
export const useFolderRiskStatistics = () => {
  // Récupération de la liste des dossiers validés
  const {
    fetchAll,
    isLoading: isValidatedFoldersLoading,
    error: validatedFoldersError,
  } = useGetValidatedFolderFiles();

  // Récupération des compteurs dashboard
  const [
    fetchFolderFilesCount,
    { data: foldersCountData, loading: foldersCountLoading, error: foldersCountError },
  ] = useFolderFilesCountLazyQuery({
    variables: {
      draftState: [Status.DRAFT],
      validationPendingState: [Status.VALIDATION_PENDING],
      validationPendingHrState: [Status.VALIDATION_PENDING_HR],
      missionCreation: [
        CustomFolderFileType.MISSION_CREATION,
        CustomFolderFileType.MISSION_CREATION_PPH,
      ],
      validatedState: [Status.VALIDATED],
    },
  });

  // Récupération des options de pays avec risque
  const { countryWithRiskOptions } = useCountryWithRiskOptions();

  // Calcul des codes de pays avec risque
  const highRiskCountryCodes = useMemo(() => {
    const codes = new Set<string>();
    countryWithRiskOptions.forEach((option) => {
      if (option.risk === CustomRiskLevel.HIGH) codes.add(option.value);
    });
    return codes;
  }, [countryWithRiskOptions]);

  const [statistics, setStatistics] = useState<ValidatedFoldersStatistics | null>(null);

  const dashboardCounts = useMemo(
    () => extractDashboardCounts(foldersCountData),
    [foldersCountData]
  );

  const fetchAndCalculate = useCallback(async (): Promise<FetchAndCalculateResult> => {
    try {
      /**
       * On récupère en parallèle :
       * - la liste complète des dossiers validés (pour les stats PDF)
       * - les 5 compteurs dashboard
       */
      const [fetchedResults, countsResult] = await Promise.all([
        fetchAll(),
        fetchFolderFilesCount(),
      ]);

      if (!fetchedResults) {
        return { statistics: null, error: validatedFoldersError ?? new Error("Erreur inconnue") };
      }

      const computedCounts = extractDashboardCounts(countsResult.data);

      const computedStatistics = calculateStatistics({
        results: fetchedResults,
        highRiskCountryCodes,
        dashboardCounts: computedCounts,
      });

      setStatistics(computedStatistics);

      if (foldersCountError) {
        return { statistics: computedStatistics, error: foldersCountError };
      }

      return { statistics: computedStatistics, error: null };
    } catch (e) {
      const err = e instanceof Error ? e : new Error("Erreur inconnue");
      return { statistics: null, error: err };
    }
  }, [
    fetchAll,
    fetchFolderFilesCount,
    validatedFoldersError,
    highRiskCountryCodes,
    foldersCountError,
  ]);

  return {
    statistics,
    dashboardCounts,
    fetchAndCalculate,
    isLoading: isValidatedFoldersLoading || foldersCountLoading,
    error: validatedFoldersError ?? foldersCountError,
  };
};
